<template>
  <!-- "Make the space yours" (altar redesign 2026-10-27; paint form redesign
       2026-10-30, Claude outputs/altar-paint-form-2026-10-30/). Paths, mood and
       their own words are pre-chosen, so one tap paints; their signs are always
       painted in. Moods are drawn scenes, the art style is shown with samples
       (Illustrated, what the server paints anyway), and every choice is sent up
       as `look` so the room behind lights up as the preview. The gold button
       rides above the bar while the choices scroll. "Paint it later" and the
       builder ("Shape it further") are always there. -->
  <div class="mk" :class="{ rm: reduced }" data-testid="altar-paint-form">
    <header class="mk-head">
      <div class="eyebrow">{{ C.paintEyebrow }}</div>
      <h2>{{ C.paintTitle }}</h2>
      <p class="lede">{{ C.paintLede }}</p>
    </header>

    <section class="sec" aria-labelledby="apf-l-paths">
      <div id="apf-l-paths" class="lab"><span>{{ C.pathsLabel }}</span></div>
      <div class="pchips">
        <button v-for="(p, i) in shownPaths" :key="p" type="button" class="pch" :class="{ on: paths.includes(p) }"
                :aria-pressed="paths.includes(p)" :style="{ animationDelay: (i >= PATHS_SHOWN ? (i - PATHS_SHOWN) * 0.04 : i * 0.04).toFixed(2) + 's' }"
                @click="togglePath(p)">{{ p }}</button>
        <button v-if="!allPaths" type="button" class="pch more-p" @click="allPaths = true">{{ C.morePaths }}</button>
      </div>
    </section>

    <section class="sec" aria-labelledby="apf-l-mood">
      <div id="apf-l-mood" class="lab has-x">
        <span>{{ C.moodLabel }}</span>
        <button type="button" class="surprise" :class="{ roll: rolling }" data-testid="altar-surprise" @click="surprise">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true" focusable="false">
            <rect x="2" y="2" width="12" height="12" rx="3" /><circle cx="5.5" cy="5.5" r=".9" fill="currentColor" />
            <circle cx="10.5" cy="10.5" r=".9" fill="currentColor" /><circle cx="8" cy="8" r=".9" fill="currentColor" />
          </svg>{{ C.surprise }}
        </button>
      </div>
      <div class="moods">
        <button v-for="m in ALTAR_MOODS" :key="m" type="button" class="mt" :class="{ on: mood === m, press: pressing === m }"
                :aria-pressed="mood === m" @click="tapMood(m)">
          <svg class="sc" viewBox="0 0 200 120" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
            <use :href="`#apf-sc-${moodLook(m)[0]}`" />
          </svg>
          <span class="ml">{{ m }}</span>
          <span class="ck" aria-hidden="true">
            <svg viewBox="0 0 12 12" focusable="false"><path d="M2.5 6.4l2.3 2.2 4.7-5" fill="none" stroke="#22133b" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </span>
        </button>
      </div>
    </section>

    <section class="sec" aria-labelledby="apf-l-style">
      <div id="apf-l-style" class="lab"><span>{{ C.styleLabel }}</span></div>
      <div class="styles">
        <button v-for="x in samples" :key="x.s" type="button" class="st" :class="{ on: style === x.s, press: pressing === x.s }"
                :aria-pressed="style === x.s" @click="tapStyle(x.s)">
          <span class="th" v-html="x.svg"></span>
          <span class="sl">{{ x.s }}</span>
        </button>
      </div>
    </section>

    <section class="sec">
      <div class="lab"><span>{{ C.wordsLabel }} <em>{{ C.wordsIf }}</em></span></div>
      <textarea v-model="words" class="ta" :aria-label="C.wordsLabel" :placeholder="C.wordsPlaceholder" maxlength="600"></textarea>
    </section>

    <div v-if="signsLine" class="signs" data-testid="altar-signs-line">
      <span v-for="g in glyphs" :key="g" class="gl">{{ g }}</span><span>{{ signsLine }}</span>
    </div>

    <p v-if="error" class="err" role="alert">{{ error }}</p>
    <div class="dock">
      <span class="orb" aria-hidden="true" v-html="orb"></span>
      <button type="button" class="btn" :class="{ go: sweeping }" :disabled="busy || !canPaint || sweeping" @click="submit">{{ C.paintNow }}</button>
    </div>
    <div class="after">
      <button type="button" class="quiet" @click="$emit('later')">{{ C.paintLater }}</button>
      <!-- The full builder and its Mystic AIda assistant, from the rite too. -->
      <button type="button" class="quiet small" @click="$emit('shape')">{{ C.shapeMore }}</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import {
  RITE_COPY as C, ALTAR_PATHS, ALTAR_MOODS, ALTAR_STYLES, PATHS_SHOWN, DEFAULT_STYLE,
  defaultPaths, moodForSigns, signsPaintedLine, signGlyphs, moodLook, surpriseLook,
} from '@/utils/altar-rite'
import { sample } from '@/utils/altar-scenes'
import { useReducedMotion } from '@/composables/useReducedMotion'

const props = defineProps({
  facets: { type: Object, default: null },   // the saved altar facets, or the choices of a painting that didn't take
  chart: { type: Object, default: null },    // { sun, moon }
  repaint: { type: Boolean, default: false }, // kept for callers; the style shows on every paint now
  busy: { type: Boolean, default: false },
  error: { type: String, default: '' },
})
const emit = defineEmits(['paint', 'later', 'shape', 'look'])
const reduced = useReducedMotion()

const f = props.facets || {}
const paths = ref(defaultPaths(f))
const mood = ref(f.mood || moodForSigns(props.chart) || 'candlelit and intimate')
// Always exactly one style: an empty one painted as Illustrated on the server anyway.
const style = ref(ALTAR_STYLES.includes(f.style) ? f.style : DEFAULT_STYLE)
const words = ref(f.belief_statement || '')
// Paths already chosen that sit past the first eight open the full list.
const allPaths = ref(paths.value.some((p) => ALTAR_PATHS.indexOf(p) >= PATHS_SHOWN))
const shownPaths = computed(() => (allPaths.value ? ALTAR_PATHS : ALTAR_PATHS.slice(0, PATHS_SHOWN)))
const signsLine = computed(() => signsPaintedLine(props.chart))
const glyphs = computed(() => signGlyphs(props.chart))
const canPaint = computed(() => paths.value.length || mood.value || words.value.trim())
// The samples are drawn in the chosen mood's light.
const samples = computed(() => ALTAR_STYLES.map((s) => ({ s, svg: sample(mood.value, s) })))
const orb = computed(() => sample(mood.value, style.value, 'sm', false))

// The room behind is the preview: it hears every choice, and once on mount.
const look = () => ({ mood: mood.value, style: style.value, paths: paths.value.slice() })
onMounted(() => emit('look', look()))
watch([mood, style, paths], () => emit('look', look()))

// A small press on the tile tapped (full motion; reduced is a crossfade).
const pressing = ref('')
let pressT = 0
function press (k) {
  clearTimeout(pressT)
  pressing.value = k
  pressT = setTimeout(() => { pressing.value = '' }, 360)
}
function togglePath (p) {
  paths.value = paths.value.includes(p) ? paths.value.filter((x) => x !== p) : [...paths.value, p]
}
function tapMood (m) {
  mood.value = mood.value === m ? '' : m     // tap again to clear, as before
  press(m)
}
function tapStyle (s) {
  style.value = s
  press(s)
}
const rolling = ref(false)
let rollT = 0
function surprise () {
  const s = surpriseLook(mood.value, style.value)
  mood.value = s.mood
  style.value = s.style
  rolling.value = false
  clearTimeout(rollT)
  requestAnimationFrame(() => { rolling.value = true; rollT = setTimeout(() => { rolling.value = false }, 650) })
}

// Paint: a light sweeps across the gold button, then the painting sequence runs.
const sweeping = ref(false)
let sweepT = 0
function submit () {
  if (props.busy || !canPaint.value || sweeping.value) return
  const facets = {
    ...(props.facets || {}),
    paths: paths.value.slice(),
    mood: mood.value,
    style: style.value || DEFAULT_STYLE,
    belief_statement: words.value.trim(),
  }
  if (reduced.value) { emit('paint', facets); return }
  sweeping.value = true
  sweepT = setTimeout(() => { sweeping.value = false; emit('paint', facets) }, 380)
}
onBeforeUnmount(() => { clearTimeout(pressT); clearTimeout(rollT); clearTimeout(sweepT) })
</script>

<style scoped>
.mk { text-align: left; }
.mk-head { text-align: center; margin-top: var(--mk-head-top, 22px); }
.eyebrow { font: 600 10px 'Cinzel', serif; letter-spacing: .2em; text-transform: uppercase; color: #f0d9a3; }
h2 { font: 500 34px/1.05 'Cormorant Garamond', 'Cormorant', Georgia, serif; color: #fff; margin: 6px 0 4px; text-shadow: 0 1px 14px rgba(0, 0, 0, .6); }
.lede { font: 17.5px/1.38 'Cormorant Garamond', 'Cormorant', Georgia, serif; color: #e6dafa; margin: 0 auto; max-width: 330px; }

/* Each label: a little more room before it, and a hairline after. */
.sec { margin-top: 26px; }
.lab { display: flex; align-items: center; gap: 10px; margin: 0 0 10px; font: 600 10.5px 'Cinzel', serif; letter-spacing: .2em;
  text-transform: uppercase; color: #cdb8f0; }
.lab::after { content: ''; flex: 1; height: 1px; background: linear-gradient(90deg, rgba(205, 184, 240, .35), transparent); }
.lab em { font: italic 15px 'Cormorant Garamond', 'Cormorant', Georgia, serif; letter-spacing: 0; text-transform: none; color: #8e7bb5; }
.lab.has-x::after { order: 1; }

/* Paths: calm chips; chosen is gold with a small ✦. */
.pchips { display: flex; flex-wrap: wrap; gap: 8px 7px; }
.pch { position: relative; border: 1px solid rgba(155, 134, 201, .32); background: rgba(20, 10, 40, .62); border-radius: 999px; padding: 5px 13px;
  font: 16px 'Cormorant Garamond', 'Cormorant', Georgia, serif; color: #dccdf3; min-height: 40px; cursor: pointer;
  transition: background .25s, border-color .25s, color .25s; animation: mk-up .45s ease both; }
.pch:hover { border-color: rgba(240, 217, 163, .55); }
.pch.on { background: linear-gradient(rgba(240, 217, 163, .22), rgba(240, 217, 163, .1)); border-color: #f0d9a3; color: #ffe7b5; padding-left: 11px; }
.pch.on::before { content: '✦'; font-size: 10px; margin-right: 6px; color: #f0d9a3; display: inline-block; animation: mk-pop .35s ease; }
.pch.more-p { border-style: dashed; color: #b9a6d6; background: transparent; }

/* Moods: little drawn scenes, the real words on each. */
.moods { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }
.mt { position: relative; height: 78px; border-radius: 14px; overflow: hidden; padding: 0; border: 1px solid rgba(155, 134, 201, .3);
  background: #0d0520; text-align: left; cursor: pointer; transition: border-color .25s ease, box-shadow .25s ease; }
.mt svg.sc { position: absolute; inset: 0; width: 100%; height: 100%; display: block; transition: transform .6s ease; }
.mt:hover svg.sc { transform: scale(1.06); }
.mt .ml { position: absolute; left: 0; right: 0; bottom: 0; padding: 16px 11px 7px; font: 17px/1.1 'Cormorant Garamond', 'Cormorant', Georgia, serif;
  color: #fff; background: linear-gradient(180deg, transparent, rgba(7, 3, 16, .78) 62%); text-shadow: 0 1px 6px #000; }
.ml::first-letter { text-transform: uppercase; }
.mt.on { border-color: #f0d9a3; box-shadow: 0 0 0 1px #f0d9a3, 0 0 22px rgba(240, 217, 163, .28); }
.mt .ck { position: absolute; right: 7px; top: 7px; width: 20px; height: 20px; border-radius: 50%; background: #f0d9a3; display: grid;
  place-items: center; transform: scale(0); transition: transform .3s cubic-bezier(.3, 1.6, .5, 1); }
.mt .ck svg { width: 11px; height: 11px; }
.mt.on .ck { transform: scale(1); }
.mt.press, .st.press { animation: mk-press .35s ease; }

/* Art styles: small samples of the same altar, in the chosen light. */
.styles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px 8px; }
.st { background: none; border: 0; padding: 0; display: flex; flex-direction: column; align-items: center; gap: 5px; min-height: 44px; cursor: pointer; }
.st .th { position: relative; width: 100%; aspect-ratio: 1; border-radius: 12px; overflow: hidden; border: 1px solid rgba(155, 134, 201, .3);
  transition: border-color .25s, box-shadow .25s; }
.st .th :deep(svg) { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
.st .sl { display: block; font: 15px/1.1 'Cormorant Garamond', 'Cormorant', Georgia, serif; color: #dccdf3; text-align: center; }
.st.on .th { border-color: #f0d9a3; box-shadow: 0 0 0 1px #f0d9a3, 0 0 18px rgba(240, 217, 163, .25); }
.st.on .sl { color: #ffe7b5; }

/* "Surprise me": a new mood and style in one tap. */
.surprise { order: 2; margin-left: auto; display: inline-flex; align-items: center; gap: 6px; background: none; border: 1px solid rgba(240, 217, 163, .4);
  border-radius: 999px; padding: 3px 10px 3px 8px; min-height: 32px; cursor: pointer; font: italic 15px 'Cormorant Garamond', 'Cormorant', Georgia, serif;
  letter-spacing: 0; text-transform: none; color: #f0d9a3; white-space: nowrap; }
.surprise svg { width: 15px; height: 15px; }
.surprise:hover { border-color: #f0d9a3; }
.surprise.roll svg { animation: mk-roll .6s ease; }

.ta { width: 100%; box-sizing: border-box; min-height: 66px; resize: vertical; border-radius: 14px; border: 1px solid rgba(240, 217, 163, .32);
  background: rgba(13, 5, 32, .6); color: #e6dafa; font: 19px/1.3 'Cormorant Garamond', 'Cormorant', Georgia, serif; padding: 11px 14px; display: block; }
.ta::placeholder { color: #7d6aa3; font-style: italic; }
.ta:focus { outline: none; border-color: #f0d9a3; }
.signs { display: flex; align-items: center; gap: 10px; margin: 22px 0 0; padding: 10px 13px; border-radius: 14px;
  background: rgba(240, 217, 163, .06); border: 1px solid rgba(240, 217, 163, .2);
  font: italic 17px 'Cormorant Garamond', 'Cormorant', Georgia, serif; color: #f0d9a3; }
.signs .gl { font: 20px 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', 'DejaVu Sans', sans-serif; color: #ffe7b5; }
.err { font: italic 16.5px 'Cormorant Garamond', 'Cormorant', Georgia, serif; color: #f0c4a3; margin: 14px 2px 0; }
.pch:focus-visible, .mt:focus-visible, .st:focus-visible, .surprise:focus-visible, .btn:focus-visible, .quiet:focus-visible, .ta:focus-visible {
  outline: 2px solid #ffe7b5; outline-offset: 2px; }

/* The gold button is always in reach: it rides above the bar while the
   choices scroll, then settles in place at the end. Same clearance as the
   rite's column foot (AltarHome .col.rite). */
.dock { position: sticky; z-index: 6; bottom: calc(96px + env(safe-area-inset-bottom, 0px)); display: flex; align-items: center; gap: 10px;
  margin-top: 16px; padding: 10px 0 0; }
.dock::before { content: ''; position: absolute; z-index: -1; left: -22px; right: -22px; top: -22px;
  bottom: calc(-1 * (108px + env(safe-area-inset-bottom, 0px))); pointer-events: none;
  background: linear-gradient(180deg, transparent, rgba(13, 5, 32, .95) 26px, rgba(13, 5, 32, .97)); }
.orb { flex: none; width: 50px; height: 50px; border-radius: 50%; overflow: hidden; border: 1px solid rgba(240, 217, 163, .55);
  box-shadow: 0 0 16px color-mix(in srgb, var(--m3, #f0d9a3) 35%, transparent); background: #0d0520; }
.orb :deep(svg) { width: 100%; height: 100%; display: block; }
.btn { flex: 1; display: block; width: 100%; border: 0; border-radius: 14px; padding: 14px; background: linear-gradient(#f3dfa8, #c9a94e);
  color: #22133b; font: 600 13.5px 'Cinzel', serif; letter-spacing: .1em; text-transform: uppercase; cursor: pointer;
  box-shadow: 0 6px 18px rgba(201, 169, 78, .3); min-height: 50px; position: relative; overflow: hidden; }
.btn::after { content: ''; position: absolute; top: 0; bottom: 0; left: -40%; width: 30%; opacity: 0; transform: skewX(-18deg);
  background: linear-gradient(100deg, transparent, rgba(255, 255, 255, .55), transparent); }
.btn.go::after { animation: mk-sweep .6s ease; }
.btn:disabled { opacity: .55; cursor: default; }
.btn.go:disabled { opacity: 1; }
.after { position: relative; z-index: 7; padding-bottom: 8px; text-align: center; }
.quiet { display: block; margin: 12px auto 0; background: none; border: 0; color: #b9a6d6; cursor: pointer; min-height: 32px;
  font: italic 17.5px 'Cormorant Garamond', 'Cormorant', Georgia, serif; text-decoration: underline; text-underline-offset: 3px; }
.quiet:hover { color: #f0d9a3; }
.quiet.small { font-size: 15.5px; margin-top: 4px; }

/* Tablet and desktop: the preview stands on the left, so no orb; the words align left. */
@container (min-width: 760px) {
  .mk-head { text-align: left; }
  .lede { margin: 0; max-width: none; }
  h2 { font-size: 36px; }
  .dock::before { left: -16px; right: -16px; }
  .orb { display: none; }
}
@container (min-width: 1000px) {
  h2 { font-size: 38px; }
  .moods { gap: 10px; }
  .mt { height: 84px; }
}

@keyframes mk-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes mk-pop { 0% { transform: scale(0); } 60% { transform: scale(1.5); } 100% { transform: scale(1); } }
@keyframes mk-press { 40% { transform: scale(.96); } }
@keyframes mk-roll { to { transform: rotate(360deg); } }
@keyframes mk-sweep { 0% { left: -40%; opacity: 1; } 100% { left: 120%; opacity: 1; } }
@keyframes mk-fade { from { opacity: 0; } to { opacity: 1; } }

/* Reduced motion: short crossfades only, nothing moves or loops. */
.rm .pch, .rm .pch.on::before, .rm .mt.press, .rm .st.press { animation: mk-fade .2s ease both; }
.rm .surprise.roll svg, .rm .btn.go::after { animation: none; }
.rm .mt:hover svg.sc { transform: none; }
@media (prefers-reduced-motion: reduce) {
  .pch, .pch.on::before, .mt.press, .st.press { animation: mk-fade .2s ease both; }
  .surprise.roll svg, .btn.go::after { animation: none; }
  .mt:hover svg.sc { transform: none; }
}
</style>
